/**
 * PDFViewerPanelReferences Component - Expandable references list
 *
 * Extracted from PDFViewerPanel.tsx
 */

import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { Theme } from '../../themes/theme';
import type { CitationData } from './types/pdf-viewer-panel.types';
import { formatPeriodForCitation } from '../../utils/formatPeriod';

interface PDFViewerPanelReferencesProps {
  citations: Record<string, CitationData>;
  expanded: boolean;
  onToggleExpanded: () => void;
  onCitationClick?: (citation: CitationData) => void;
  theme: Theme;
}

export const PDFViewerPanelReferences: React.FC<PDFViewerPanelReferencesProps> = ({
  citations,
  expanded,
  onToggleExpanded,
  onCitationClick,
  theme,
}) => {
  if (!citations || Object.keys(citations).length === 0) {
    return null;
  }

  const citationCount = Object.keys(citations).length;

  const getDisplayName = (citation: CitationData) => {
    if (citation.company_name && citation.document_type) {
      let name = `${citation.company_name} - ${citation.document_type}`;
      name += formatPeriodForCitation(citation.document_period, citation.document_type);
      return name;
    }
    return citation.document_display_name || citation.document_name;
  };

  const uniqueDocs = new Map<string, CitationData>();
  Object.values(citations).forEach((citation) => {
    const docName = getDisplayName(citation);
    if (!uniqueDocs.has(docName)) {
      uniqueDocs.set(docName, citation);
    }
  });

  const docNames = Array.from(uniqueDocs.keys());
  const summaryText = docNames.slice(0, 2).join(', ') + (docNames.length > 2 ? '...' : '');

  return (
    <div className="mt-3 text-xs">
      <button
        onClick={onToggleExpanded}
        className={`flex items-center justify-between w-full ${theme.colors.text.secondary} hover:${theme.colors.text.primary} transition-colors`}
      >
        <span className="font-medium truncate">
          Referencias ({citationCount}): {summaryText}
        </span>
        <span
          className={`flex items-center space-x-1 ${theme.colors.text.secondary} flex-shrink-0 ml-2`}
        >
          <span className="text-[10px]">Ver detalles</span>
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </span>
      </button>

      {expanded && (
        <div
          className={`mt-2 p-3 ${theme.colors.bg.tertiary} rounded-lg border ${theme.colors.border.primary} animate-fade-in space-y-1.5`}
        >
          {Object.entries(citations).map(([number, citation]) => (
            <button
              key={number}
              onClick={() => onCitationClick?.(citation)}
              className={`w-full text-left p-2 rounded ${theme.colors.bg.secondary} hover:${theme.colors.bg.tertiary} border ${theme.colors.border.primary}/50 hover:${theme.colors.border.focus} transition-all group`}
            >
              <div className="flex items-start space-x-2">
                <span
                  className={`flex-shrink-0 w-5 h-5 flex items-center justify-center ${theme.colors.bg.tertiary} ${theme.colors.text.inverse} rounded text-xs font-semibold`}
                >
                  {number}
                </span>
                <div className="flex-1 min-w-0">
                  <p
                    className={`${theme.colors.text.primary} group-hover:${theme.colors.text.inverse} font-medium truncate`}
                  >
                    {getDisplayName(citation)}
                  </p>
                  <p className={`${theme.colors.text.secondary} text-xs mt-0.5`}>
                    Página {citation.page}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
